/**
 * Le dossier du lecteur de /outils : ce qu'il a dit une fois, et que chaque
 * outil reprend.
 *
 * UNE SEULE LANGUE POUR TOUTE LA RUBRIQUE. Les outils sont nés un par un, et
 * chacun a nommé ses réponses à sa façon : « eurl » désigne l'EURL à l'impôt
 * sur les sociétés dans « Rémunération ou dividendes » et l'EURL à l'impôt sur
 * le revenu dans « Passer à l'IS », « services » veut dire BIC prestations en
 * micro et « commerce ou prestations » en société, les clients s'écrivent de
 * quatre manières. Renommer l'intérieur de quarante outils aurait touché des
 * moteurs testés pour un gain nul. Le dossier parle donc SA langue, une seule,
 * et chaque outil a ici son traducteur, dans les deux sens : ce que le dossier
 * lui donne au départ (`entree`), ce que ses réponses disent du dossier
 * (`sortie`). Une incohérence ne se corrige qu'à un endroit, celui-ci.
 *
 * LE DOSSIER NE DEVINE RIEN. Un champ inconnu reste null, et l'outil garde sa
 * valeur par défaut et sa question. Un traducteur qui ne sait pas trancher
 * (une activité « commerce ou prestations » qui peut être de la vente ou des
 * services) ne renvoie rien plutôt qu'une valeur fausse.
 *
 * DEUX DOSSIERS, PAS UN. La situation (statut, activité, chiffres, foyer) sert
 * aux simulateurs et peut suivre le compte. L'identité (nom, adresse, SIRET,
 * naissance) ne sert qu'aux formulaires et ne quitte jamais l'appareil : elle
 * a sa propre clé et son propre nettoyage. Le numéro de sécurité sociale n'y
 * entre jamais, faute de raison de le garder.
 *
 * Ce module est pur : ni React, ni stockage. Il se teste seul
 * (scripts/check-dossier.mjs).
 */

export const CLE_DOSSIER = "believemy-profil-solopreneur";
export const CLE_IDENTITE = "believemy-identite";

/**
 * Les statuts du dossier.
 *   - projet : rien n'est déclaré, les calculs sont ceux de la micro ;
 *   - micro ;
 *   - reel : entreprise individuelle au réel ;
 *   - eurl : EURL à l'impôt sur les sociétés (le cas par défaut d'une EURL
 *     dont l'associé est une personne physique s'est inversé : l'IR est le
 *     régime de droit, mais presque tous les gérants optent pour l'IS, et
 *     c'est ce que la phrase d'accueil calculait déjà sous ce nom) ;
 *   - eurlIr : EURL restée à l'impôt sur le revenu ;
 *   - sasu.
 */
export const STATUTS_DOSSIER = ["projet", "micro", "reel", "eurl", "eurlIr", "sasu"];

/** Les quatre activités, sous les noms des simulateurs de la micro. */
export const ACTIVITES_DOSSIER = ["vente", "services", "bnc", "cipav"];

export const CHAMPS_SITUATION = [
    "statut",
    "activite",
    "artisan",
    "dateDebut",
    "caCetteAnnee",
    "caAnDernier",
    "enCouple",
    "enfants",
    "autresRevenus",
    "objectifMensuel",
    "fraisMensuels",
    "tarifJournalier",
    "optionFiscale",
    "acre",
    "tvaFacturee",
];

export const DOSSIER_VIDE = {
    statut: null,
    activite: null,
    artisan: null,
    dateDebut: null,
    caCetteAnnee: null,
    caAnDernier: null,
    enCouple: null,
    enfants: null,
    autresRevenus: null,
    objectifMensuel: null,
    fraisMensuels: null,
    tarifJournalier: null,
    optionFiscale: null,
    // L'ACRE obtenue (ou demandée, pour un projet) : les cotisations de la
    // micro sont réduites jusqu'à la fin du troisième trimestre civil qui
    // suit le début d'activité (finAcre, lib/tools/incomeTax.js).
    acre: null,
    // La TVA déjà facturée à ses clients (par option, ou le seuil passé) :
    // la page n'annonce plus son arrivée.
    tvaFacturee: null,
    // Le jour où le chiffre d'affaires a été donné ou confirmé : c'est de lui
    // que dépend la date de passage du seuil de TVA, et la page le rappelle
    // quand il vieillit.
    caLe: null,
    // Ce que le lecteur a encaissé, mois par mois : { "2026-01": 3800, … }.
    // Quand il les donne, `caCetteAnnee` devient le rythme annuel qu'ils
    // dessinent (rythmeDesEncaissements), et la date du seuil de TVA se
    // calcule sur les vrais mois au lieu d'une moyenne.
    encaissements: {},
    // Les démarches que le lecteur a déclarées faites, par échéance :
    // { "acre-demande@2026-09-30": "2026-09-12" }. Une alerte faite passe dans
    // « En ordre » au lieu de revenir à chaque visite.
    faits: {},
    // Horodatage de la dernière écriture : c'est lui qui départage l'appareil
    // et le compte quand les deux ont changé.
    majLe: null,
};

const DATE_ISO = /^\d{4}-\d{2}-\d{2}$/;

function montant(v, { positif = false } = {}) {
    if (typeof v === "string" && v.trim() !== "") v = Number(v);
    if (!Number.isFinite(v) || v < 0 || v > 100000000) return null;
    if (positif && v === 0) return null;
    return Math.round(v);
}

function booleen(v) {
    return typeof v === "boolean" ? v : null;
}

function dateValide(v) {
    if (typeof v !== "string" || !DATE_ISO.test(v)) return null;
    const [a, m, j] = v.split("-").map(Number);
    const d = new Date(Date.UTC(a, m - 1, j));
    return d.getUTCFullYear() === a && d.getUTCMonth() === m - 1 && d.getUTCDate() === j && a >= 1950 && a <= 2100
        ? v
        : null;
}

/** Les démarches faites : au plus quarante, chacune « clé@date » → date du jour où elle a été faite. */
const CLE_FAIT = /^[a-z0-9-]{1,40}@\d{4}-\d{2}-\d{2}$/;
function nettoyerFaits(brut) {
    if (!brut || typeof brut !== "object" || Array.isArray(brut)) return {};
    return Object.fromEntries(
        Object.entries(brut)
            .filter(([cle, jour]) => CLE_FAIT.test(cle) && dateValide(jour))
            .slice(-40),
    );
}

/** Les encaissements : au plus vingt-quatre mois, les plus récents, chacun « AAAA-MM » → montant. */
const CLE_MOIS = /^\d{4}-(0[1-9]|1[0-2])$/;
function nettoyerEncaissements(brut) {
    if (!brut || typeof brut !== "object" || Array.isArray(brut)) return {};
    return Object.fromEntries(
        Object.entries(brut)
            .filter(([mois]) => CLE_MOIS.test(mois))
            .map(([mois, v]) => [mois, montant(v)])
            .filter(([, v]) => v !== null)
            .sort(([a], [b]) => (a < b ? -1 : 1))
            .slice(-24),
    );
}

/**
 * Les mois où l'on peut encaisser cette année, « AAAA-MM » : de janvier, ou du
 * mois de début d'activité s'il tombe cette année, jusqu'au mois en cours.
 */
export function moisDeLAnnee(annee, moisEnCours, dateDebut) {
    const debut = dateDebut && Number(dateDebut.slice(0, 4)) === annee ? Number(dateDebut.slice(5, 7)) : 1;
    const liste = [];
    for (let m = debut; m <= moisEnCours; m++) liste.push(`${annee}-${String(m).padStart(2, "0")}`);
    return liste;
}

/**
 * Ce que disent les encaissements de l'année : le total, le nombre de mois
 * couverts, le dernier mois donné et le rythme annuel qui en découle.
 *
 * LES MOIS COUVERTS VONT DU PREMIER MOIS D'ACTIVITÉ AU DERNIER MOIS DONNÉ. Un
 * mois laissé vide entre les deux compte pour zéro : c'est un mois sans
 * encaissement, pas un mois inconnu. Le mois en cours ne compte que s'il est
 * rempli. Rien de donné cette année : null, et le chiffre annuel tapé reste.
 */
export function rythmeDesEncaissements(encaissements, annee, dateDebut) {
    const prefixe = `${annee}-`;
    const donnes = Object.keys(encaissements || {})
        .filter((m) => m.startsWith(prefixe))
        .sort();
    if (!donnes.length) return null;
    const dernier = donnes[donnes.length - 1];
    const mois = moisDeLAnnee(annee, Number(dernier.slice(5, 7)), dateDebut);
    if (!mois.length) return null;
    const total = mois.reduce((s, m) => s + (encaissements[m] || 0), 0);
    return { total, mois: mois.length, dernier, rythme: Math.round((total / mois.length) * 12) };
}

/** Ne garde que les champs connus, chacun dans ses valeurs permises. */
export function nettoyerDossier(brut) {
    if (!brut || typeof brut !== "object") return { ...DOSSIER_VIDE };
    const enfants = Number.isInteger(brut.enfants) && brut.enfants >= 0 && brut.enfants <= 10 ? brut.enfants : null;
    return {
        statut: STATUTS_DOSSIER.includes(brut.statut) ? brut.statut : null,
        activite: ACTIVITES_DOSSIER.includes(brut.activite) ? brut.activite : null,
        artisan: booleen(brut.artisan),
        dateDebut: dateValide(brut.dateDebut),
        caCetteAnnee: montant(brut.caCetteAnnee),
        caAnDernier: montant(brut.caAnDernier),
        enCouple: booleen(brut.enCouple),
        enfants,
        autresRevenus: montant(brut.autresRevenus),
        objectifMensuel: montant(brut.objectifMensuel, { positif: true }),
        fraisMensuels: montant(brut.fraisMensuels),
        tarifJournalier: montant(brut.tarifJournalier, { positif: true }),
        optionFiscale: brut.optionFiscale === "bareme" || brut.optionFiscale === "vfl" ? brut.optionFiscale : null,
        acre: booleen(brut.acre),
        tvaFacturee: booleen(brut.tvaFacturee),
        caLe: dateValide(brut.caLe),
        encaissements: nettoyerEncaissements(brut.encaissements),
        faits: nettoyerFaits(brut.faits),
        majLe: typeof brut.majLe === "string" && !Number.isNaN(Date.parse(brut.majLe)) ? brut.majLe : null,
    };
}

/** Vrai tant que le lecteur n'a rien dit. */
export function dossierVide(d) {
    return !d || CHAMPS_SITUATION.every((c) => d[c] === null || d[c] === undefined);
}

/** Le nombre de questions du dossier auxquelles le lecteur a répondu, sur six. */
export function questionsRepondues(d) {
    if (!d) return 0;
    return [
        d.statut !== null,
        d.activite !== null,
        d.dateDebut !== null || d.statut === "projet",
        d.caCetteAnnee !== null,
        d.enCouple !== null && d.enfants !== null,
        d.objectifMensuel !== null,
    ].filter(Boolean).length;
}

// ---------------------------------------------------------------------------
// L'identité, pour les formulaires
// ---------------------------------------------------------------------------

export const CHAMPS_IDENTITE = [
    "civilite",
    "prenoms",
    "nom",
    "nomNaissance",
    "dateNaissance",
    "villeNaissance",
    "paysNaissance",
    "nationalite",
    "voie",
    "complement",
    "codePostal",
    "commune",
    "email",
    "telephone",
    "siret",
    "codeApe",
];

export const IDENTITE_VIDE = Object.fromEntries(CHAMPS_IDENTITE.map((c) => [c, null]));

function texte(v, max = 200) {
    if (typeof v !== "string") return null;
    const t = v.replace(/\s+/g, " ").trim();
    return t ? t.slice(0, max) : null;
}

export function nettoyerIdentite(brut) {
    if (!brut || typeof brut !== "object") return { ...IDENTITE_VIDE };
    const siret = typeof brut.siret === "string" ? brut.siret.replace(/\s/g, "") : "";
    return {
        civilite: brut.civilite === "madame" || brut.civilite === "monsieur" ? brut.civilite : null,
        prenoms: texte(brut.prenoms, 120),
        nom: texte(brut.nom, 120),
        nomNaissance: texte(brut.nomNaissance, 120),
        dateNaissance: dateValide(brut.dateNaissance),
        villeNaissance: texte(brut.villeNaissance, 120),
        paysNaissance: texte(brut.paysNaissance, 80),
        nationalite: texte(brut.nationalite, 80),
        voie: texte(brut.voie, 200),
        complement: texte(brut.complement, 200),
        codePostal: texte(brut.codePostal, 12),
        commune: texte(brut.commune, 120),
        email: texte(brut.email, 190),
        telephone: texte(brut.telephone, 30),
        siret: /^\d{9}(\d{5})?$/.test(siret) ? siret : null,
        codeApe: texte(brut.codeApe, 8),
    };
}

export function identiteVide(i) {
    return !i || CHAMPS_IDENTITE.every((c) => !i[c]);
}

/** L'adresse sur une ligne, pour les formulaires qui n'ont qu'un champ. */
export function adresseEnLigne(i) {
    if (!i) return null;
    const ville = [i.codePostal, i.commune].filter(Boolean).join(" ");
    const morceaux = [i.voie, i.complement, ville].filter(Boolean);
    return morceaux.length ? morceaux.join(", ") : null;
}

/**
 * L'adresse sur plusieurs lignes, pour les en-têtes de lettre et de devis :
 * leur champ se découpe par retour à la ligne (rue, puis code postal et
 * commune).
 */
export function adresseEnLignes(i) {
    if (!i) return null;
    const ville = [i.codePostal, i.commune].filter(Boolean).join(" ");
    const morceaux = [i.voie, i.complement, ville].filter(Boolean);
    return morceaux.length ? morceaux.join("\n") : null;
}

/** Le SIRET complet seulement : un SIREN seul ferait échouer les champs qui en attendent quatorze chiffres. */
function siretComplet(i) {
    return /^\d{14}$/.test(i?.siret || "") ? i.siret : null;
}

/** Le SIREN, s'il est connu : les neuf premiers chiffres du SIRET. */
export function sirenDe(i) {
    return i?.siret ? i.siret.slice(0, 9) : null;
}

// ---------------------------------------------------------------------------
// Les traducteurs
// ---------------------------------------------------------------------------

/** Retire les valeurs inconnues : un outil ne reçoit que ce qu'on sait. */
function defini(objet) {
    return Object.fromEntries(Object.entries(objet).filter(([, v]) => v !== null && v !== undefined));
}

function anneeDe(iso) {
    return iso ? Number(iso.slice(0, 4)) : null;
}

const EN_MICRO = new Set(["projet", "micro"]);
const EN_SOCIETE = new Set(["eurl", "eurlIr", "sasu"]);

/** Le statut connu vaut-il pour un outil de la micro ? Inconnu, on suppose que oui. */
function microOuInconnu(d) {
    return d.statut === null || EN_MICRO.has(d.statut);
}

/** Le bénéfice d'une année : le chiffre d'affaires moins les frais connus. */
function benefice(d) {
    if (d.caCetteAnnee === null) return null;
    return Math.max(0, Math.round(d.caCetteAnnee - (d.fraisMensuels || 0) * 12));
}

/**
 * L'activité dans le vocabulaire des outils de société : services
 * (commerce ou prestations), artisan, bnc, cipav. La vente y est un commerce.
 */
function activiteSociete(d) {
    if (d.activite === "services") return d.artisan ? "artisan" : "services";
    if (d.activite === "vente") return "services";
    return d.activite;
}

/** Et dans l'autre sens : « services » en société ne tranche pas entre vente et prestations. */
function activiteDepuisSociete(a, d) {
    if (a === "artisan") return { activite: "services", artisan: true };
    if (a === "bnc" || a === "cipav") return { activite: a };
    if (a === "services") {
        return d.activite === "vente" || d.activite === "services" ? {} : { activite: "services" };
    }
    return {};
}

/**
 * Le foyer, commun à dix outils.
 *
 * LES OUTILS NE COMPTENT PAS LES ENFANTS JUSQU'AU MÊME NOMBRE : des boutons
 * de zéro à « trois et plus » ici, un curseur jusqu'à six là. Le dossier en
 * garde le vrai nombre ; chaque outil reçoit son maximum, et la réponse
 * « trois » d'un outil qui s'arrête à trois ne remplace pas les cinq du
 * dossier.
 */
function foyerEntree(d, maxEnfants = 6) {
    return {
        enCouple: d.enCouple,
        enfants: d.enfants === null ? null : Math.min(d.enfants, maxEnfants),
        autresRevenus: d.autresRevenus,
    };
}
function foyerSortie(v, d = DOSSIER_VIDE, maxEnfants = 6) {
    return defini({
        enCouple: typeof v.enCouple === "boolean" ? v.enCouple : null,
        enfants:
            Number.isInteger(v.enfants) && !(v.enfants === maxEnfants && (d?.enfants ?? 0) > maxEnfants)
                ? v.enfants
                : null,
        autresRevenus: Number.isFinite(v.autresRevenus) ? v.autresRevenus : null,
    });
}

function activiteMicro(d, permises = ACTIVITES_DOSSIER) {
    return permises.includes(d.activite) ? d.activite : null;
}

/** L'ancienneté que lit l'outil des plafonds, déduite de la date de début. */
function ancienneteDe(d, annee) {
    const debut = anneeDe(d.dateDebut);
    if (d.statut === "projet") return "premiere";
    if (!debut) return null;
    if (debut >= annee) return "premiere";
    if (debut === annee - 1) return "deuxieme";
    return "etablie";
}

/**
 * Un traducteur par composant d'outil.
 *
 *   - `entree(dossier, { annee })` rend les valeurs de départ de l'outil, sous
 *     SES noms, et seulement celles que le dossier connaît ;
 *   - `sortie(valeurs, dossier)` rend ce que les réponses de l'outil disent
 *     du dossier, sous les noms du dossier, et seulement ce qui s'en déduit
 *     sans hésitation.
 *
 * Un outil absent de cette table ne se remplit pas : c'est le cas de ceux
 * dont aucune question ne recoupe le dossier (congé parental, assurances,
 * requalification…).
 */
export const TRADUCTEURS = {
    SimulateurRevenuNet: {
        entree: (d) =>
            microOuInconnu(d)
                ? {
                      activite: activiteMicro(d),
                      artisan: d.artisan,
                      ...foyerEntree(d, 3),
                      optionFiscale: d.optionFiscale,
                      ca: d.caCetteAnnee,
                  }
                : {},
        sortie: (v, d) =>
            defini({
                activite: v.activite,
                artisan: v.activite === "vente" || v.activite === "services" ? v.artisan : null,
                ...foyerSortie(v, d, 3),
                optionFiscale: v.optionFiscale,
                caCetteAnnee: v.ca,
            }),
    },

    CombienFacturer: {
        entree: (d) =>
            microOuInconnu(d)
                ? {
                      // L'outil ne calcule pas la vente : il renvoie ailleurs.
                      activite: activiteMicro(d, ["bnc", "services", "cipav"]),
                      artisan: d.artisan,
                      netMensuel: d.objectifMensuel,
                      fraisMensuels: d.fraisMensuels,
                      ...foyerEntree(d, 3),
                      optionFiscale: d.optionFiscale,
                  }
                : {},
        sortie: (v, d) =>
            defini({
                activite: v.activite,
                artisan: v.activite === "vente" || v.activite === "services" ? v.artisan : null,
                objectifMensuel: v.netMensuel,
                fraisMensuels: v.fraisMensuels,
                ...foyerSortie(v, d, 3),
                optionFiscale: v.optionFiscale,
            }),
    },

    AbonnesSaas: {
        entree: (d) => ({
            statut: EN_MICRO.has(d.statut) ? "micro" : d.statut === "eurl" || d.statut === "sasu" ? d.statut : null,
            activite: EN_MICRO.has(d.statut) || d.statut === null ? activiteMicro(d, ["bnc", "services"]) : null,
            netMensuel: d.objectifMensuel,
            fraisMensuels: d.fraisMensuels,
        }),
        sortie: (v, d) =>
            defini({
                // « micro » dans l'outil ne dit pas si le projet est déclaré.
                statut: v.statut === "micro" ? (EN_MICRO.has(d.statut) ? d.statut : "micro") : v.statut,
                activite: v.statut === "micro" ? v.activite : null,
                objectifMensuel: v.netMensuel,
                fraisMensuels: v.fraisMensuels,
            }),
    },

    ComparateurRegimes: {
        entree: (d) => ({
            activite: activiteMicro(d, ["vente", "services", "bnc"]),
            chiffreAffaires: d.caCetteAnnee,
            charges: d.fraisMensuels !== null ? d.fraisMensuels * 12 : null,
            ...foyerEntree(d),
        }),
        sortie: (v, d) =>
            defini({
                activite: v.activite,
                caCetteAnnee: v.chiffreAffaires,
                fraisMensuels: Number.isFinite(v.charges) ? Math.round(v.charges / 12) : null,
                ...foyerSortie(v, d),
            }),
    },

    QuandQuitterLaMicro: {
        entree: (d) => ({
            activite: activiteMicro(d, ["vente", "services", "bnc"]),
            chiffreAffaires: d.caCetteAnnee,
            charges: d.fraisMensuels !== null ? d.fraisMensuels * 12 : null,
            ...foyerEntree(d),
        }),
        sortie: (v, d) =>
            defini({
                activite: v.activite,
                caCetteAnnee: v.chiffreAffaires,
                fraisMensuels: Number.isFinite(v.charges) ? Math.round(v.charges / 12) : null,
                ...foyerSortie(v, d),
            }),
    },

    Trajectoire: {
        entree: (d) => ({
            activite: activiteMicro(d, ["vente", "services", "bnc"]),
            chiffreAffaires: d.caCetteAnnee,
            part:
                d.fraisMensuels !== null && d.caCetteAnnee
                    ? Math.min(80, Math.round(((d.fraisMensuels * 12) / d.caCetteAnnee) * 100))
                    : null,
            besoin: d.objectifMensuel !== null ? d.objectifMensuel * 12 : null,
            enCouple: d.enCouple,
            enfants: d.enfants === null ? null : Math.min(d.enfants, 6),
        }),
        sortie: (v, d) => {
            const { enCouple, enfants } = foyerSortie(v, d);
            return defini({
                activite: v.activite,
                caCetteAnnee: v.chiffreAffaires,
                objectifMensuel: Number.isFinite(v.besoin) && v.besoin > 0 ? Math.round(v.besoin / 12) : null,
                enCouple,
                enfants,
            });
        },
    },

    SimulateurVersementLiberatoire: {
        entree: (d, { annee }) =>
            microOuInconnu(d)
                ? {
                      activite: activiteMicro(d, ["vente", "services", "bnc"]),
                      ...foyerEntree(d, 3),
                      // L'année de création a sa propre règle : l'option se
                      // prend jusqu'à la fin du troisième mois d'activité.
                      situation:
                          d.statut === "projet" || anneeDe(d.dateDebut) === annee
                              ? "creation"
                              : d.optionFiscale,
                      ca: d.caCetteAnnee,
                  }
                : {},
        sortie: (v, d) =>
            defini({
                activite: v.activite,
                ...foyerSortie(v, d, 3),
                optionFiscale: v.situation === "bareme" || v.situation === "vfl" ? v.situation : null,
                caCetteAnnee: v.ca,
            }),
    },

    SimulateurAcre: {
        entree: (d) =>
            microOuInconnu(d)
                ? {
                      creee: d.statut === "projet" ? false : d.statut === "micro" ? true : null,
                      // Pour un projet, la date prévue : l'écran la demande aussi.
                      dateDebut: EN_MICRO.has(d.statut) ? d.dateDebut : null,
                      activite: activiteMicro(d),
                      ca: d.caCetteAnnee,
                  }
                : {},
        // Le chiffre d'affaires de l'outil est celui de la période aidée, pas
        // de l'année civile : il ne remonte pas dans le dossier.
        sortie: (v, d) =>
            defini({
                statut: v.creee === false ? "projet" : v.creee === true && EN_MICRO.has(d.statut ?? "projet") ? "micro" : null,
                dateDebut: v.creee !== null && v.creee !== undefined ? v.dateDebut : null,
                activite: v.activite,
            }),
    },

    SimulateurIndemnitesJournalieres: {
        // L'outil lit un chiffre d'affaires annuel MOYEN sur trois ans : on le
        // prérempli avec celui de l'année, mais on ne le renvoie pas.
        entree: (d) => (microOuInconnu(d) ? { activite: activiteMicro(d), ca: d.caCetteAnnee } : {}),
        sortie: (v) => defini({ activite: v.activite }),
    },

    SimulateurPlafonds: {
        entree: (d, { annee }) =>
            microOuInconnu(d)
                ? {
                      anciennete: ancienneteDe(d, annee),
                      // Dans l'outil, « cipav » désigne une activité MIXTE affiliée à
                      // la Cipav ; un libéral, réglementé ou non, y choisit les
                      // prestations.
                      activite: activiteMicro(d, ["vente", "services"]) ?? (d.activite === "bnc" || d.activite === "cipav" ? "services" : null),
                      dateDebut: anneeDe(d.dateDebut) === annee ? d.dateDebut : null,
                      caAnDernier: d.caAnDernier,
                      caCetteAnnee: d.caCetteAnnee,
                  }
                : {},
        // L'outil range le libéral non réglementé avec les prestations : sa
        // réponse ne peut pas dire au dossier si c'est du BNC ou du BIC.
        sortie: (v, d) =>
            defini({
                activite:
                    v.activite === "vente"
                        ? "vente"
                        : v.activite === "services" && d.activite !== "bnc" && d.activite !== "cipav"
                          ? "services"
                          : null,
                dateDebut: v.anciennete === "premiere" ? v.dateDebut : null,
                caAnDernier: v.anciennete !== "premiere" ? v.caAnDernier : null,
                caCetteAnnee: v.caCetteAnnee,
            }),
    },

    SimulateurSeuilsTva: {
        entree: (d, { annee }) => {
            const debut = anneeDe(d.dateDebut);
            return {
                categorie: d.activite === "vente" ? "global" : d.activite === "services" || d.activite === "bnc" ? "services" : null,
                situation:
                    d.statut === "projet" || (debut !== null && debut > annee)
                        ? "projet"
                        : debut === annee
                          ? "creee"
                          : debut
                            ? "etablie"
                            : null,
                caN1: debut !== null && debut < annee ? d.caAnDernier : null,
                // L'outil demande ce qui est encaissé depuis le 1er janvier, à
                // ce jour ; le dossier connaît l'année entière. On ne mélange pas.
                caEnCours: null,
                debutEnCours: debut === annee ? d.dateDebut : null,
                debutN1: debut === annee - 1 ? d.dateDebut : null,
            };
        },
        sortie: (v, d) =>
            defini({
                activite:
                    v.categorie === "global"
                        ? "vente"
                        : v.categorie === "services" && d.activite !== "bnc" && d.activite !== "cipav"
                          ? "services"
                          : null,
                caAnDernier: v.situation === "etablie" ? v.caN1 : null,
            }),
    },

    SimulateurTrimestresRetraite: {
        entree: (d) => (microOuInconnu(d) ? { activite: activiteMicro(d), ca: d.caCetteAnnee } : {}),
        sortie: (v) => defini({ activite: v.activite, caCetteAnnee: v.ca }),
    },

    RemunerationOuDividendes: {
        entree: (d) => ({
            forme: EN_SOCIETE.has(d.statut) ? d.statut : null,
            activite: activiteSociete(d),
            enveloppe: EN_SOCIETE.has(d.statut) ? benefice(d) : null,
            ...foyerEntree(d),
        }),
        sortie: (v, d) =>
            defini({
                statut: v.forme,
                ...activiteDepuisSociete(v.activite, d),
                ...foyerSortie(v, d),
            }),
    },

    EurlOuSasu: {
        entree: (d) => ({
            activite: activiteSociete(d),
            enveloppe: EN_SOCIETE.has(d.statut) || d.statut === "reel" ? benefice(d) : null,
            ...foyerEntree(d),
        }),
        sortie: (v, d) => defini({ ...activiteDepuisSociete(v.activite, d), ...foyerSortie(v, d) }),
    },

    PasserIs: {
        // Ici « eurl » est l'EURL à l'impôt sur le revenu : c'est elle qui
        // se pose la question de l'IS.
        entree: (d) => ({
            forme: { reel: "ei", eurlIr: "eurl", micro: "micro" }[d.statut] ?? null,
            activite: activiteSociete(d),
            benefice: d.statut === "reel" || d.statut === "eurlIr" ? benefice(d) : null,
            besoin: d.objectifMensuel !== null ? d.objectifMensuel * 12 : null,
            ...foyerEntree(d),
        }),
        sortie: (v, d) =>
            defini({
                statut: { ei: "reel", eurl: "eurlIr", micro: EN_MICRO.has(d.statut) ? d.statut : "micro" }[v.forme] ?? null,
                ...activiteDepuisSociete(v.activite, d),
                objectifMensuel: Number.isFinite(v.besoin) && v.besoin > 0 ? Math.round(v.besoin / 12) : null,
                ...foyerSortie(v, d),
            }),
    },

    CombienMeVerser: {
        // Le bénéfice demandé ici est celui de l'exercice À CE JOUR, pas celui
        // de l'année : le dossier ne le connaît pas.
        entree: (d) => ({
            forme: { eurl: "eurl", sasu: "sasu", reel: "ir", eurlIr: "ir" }[d.statut] ?? null,
            activite: activiteSociete(d),
        }),
        // « ir » mêle l'entreprise individuelle et l'EURL à l'IR : rien à en tirer.
        sortie: (v, d) =>
            defini({
                statut: v.forme === "eurl" || v.forme === "sasu" ? v.forme : null,
                ...activiteDepuisSociete(v.activite, d),
            }),
    },

    ArretMaladieSociete: {
        entree: (d) => ({
            forme: d.statut === "sasu" ? "sasu" : d.statut === "eurl" || d.statut === "eurlIr" ? "eurl" : EN_MICRO.has(d.statut) ? "micro" : null,
            reglemente: d.activite ? d.activite === "cipav" : null,
        }),
        sortie: (v, d) =>
            defini({
                statut: v.forme === "sasu" ? "sasu" : v.forme === "eurl" && !(d.statut === "eurl" || d.statut === "eurlIr") ? "eurl" : null,
            }),
    },

    IndemnitesKilometriques: {
        entree: (d) => ({
            statut:
                d.statut === "reel"
                    ? d.activite === "bnc" || d.activite === "cipav"
                        ? "bnc"
                        : d.activite
                          ? "bic"
                          : null
                    : EN_MICRO.has(d.statut)
                      ? "micro"
                      : d.statut,
        }),
        sortie: (v, d) =>
            defini({
                statut:
                    v.statut === "bnc" || v.statut === "bic"
                        ? "reel"
                        : v.statut === "micro"
                          ? EN_MICRO.has(d.statut)
                              ? d.statut
                              : "micro"
                          : v.statut,
            }),
    },

    Holding: {
        // L'outil demande le bénéfice APRÈS la rémunération du dirigeant : le
        // dossier ne le connaît pas, la question reste posée.
        entree: (d) => ({
            forme: EN_SOCIETE.has(d.statut) ? d.statut : EN_MICRO.has(d.statut) ? "micro" : null,
            activite: activiteSociete(d),
        }),
        sortie: (v, d) =>
            defini({
                statut: v.forme === "micro" ? (EN_MICRO.has(d.statut) ? d.statut : "micro") : v.forme,
                ...activiteDepuisSociete(v.activite, d),
            }),
    },

    EligibiliteVersementLiberatoire: {
        entree: (d, { annee }) =>
            microOuInconnu(d)
                ? {
                      situation: (() => {
                          const debut = anneeDe(d.dateDebut);
                          if (debut === annee) return "creeCetteAnnee";
                          if (debut !== null && debut > annee) return "creeAnProchain";
                          if (d.statut === "projet") return "creeAnProchain";
                          return debut !== null ? "actif" : null;
                      })(),
                      enCouple: d.enCouple,
                      enfants: d.enfants,
                  }
                : {},
        sortie: (v) =>
            defini({
                enCouple: typeof v.enCouple === "boolean" ? v.enCouple : null,
                enfants: Number.isInteger(v.enfants) ? v.enfants : null,
            }),
    },

    EcheancesSociete: {
        entree: (d, { annee }) => ({
            forme: d.statut === "eurl" || d.statut === "sasu" ? d.statut : d.statut === "eurlIr" || d.statut === "reel" ? "ir" : null,
            creation: EN_SOCIETE.has(d.statut) ? d.dateDebut : null,
            premier: EN_SOCIETE.has(d.statut) && d.dateDebut ? anneeDe(d.dateDebut) === annee : null,
        }),
        sortie: (v, d) =>
            defini({
                statut: v.forme === "eurl" || v.forme === "sasu" ? v.forme : null,
                dateDebut: (v.forme === "eurl" || v.forme === "sasu") && EN_SOCIETE.has(d.statut ?? v.forme) ? v.creation || null : null,
            }),
    },

    MediateurConsommation: {
        entree: (d) => ({
            // Un projet peut encore devenir une société : on ne répond pas pour lui.
            forme: EN_SOCIETE.has(d.statut) ? "societe" : d.statut && d.statut !== "projet" ? "personnePhysique" : null,
        }),
        sortie: () => ({}),
    },

    RepEmballages: {
        entree: (d) => ({
            // Un projet peut encore devenir une société : on ne répond pas pour lui.
            forme: EN_SOCIETE.has(d.statut) ? "societe" : d.statut && d.statut !== "projet" ? "personnePhysique" : null,
        }),
        sortie: () => ({}),
    },
};

/**
 * Les traducteurs d'identité, un par formulaire. `entree(identite)` rend les
 * champs du formulaire sous ses noms ; `sortie(valeurs)` ce qu'ils disent de
 * l'identité. Les formulaires qui distinguent nom de naissance et nom d'usage
 * reçoivent le nom de naissance quand il est connu, le nom sinon.
 */
/** Le nom qu'on porte : le nom d'usage s'il est connu, le nom de naissance sinon. */
function nomUsuel(i) {
    return i.nom || i.nomNaissance;
}
function identiteCommune(i) {
    return { civilite: i.civilite, prenoms: i.prenoms, nom: nomUsuel(i) };
}
/**
 * Les documents qui exigent le nom de naissance (déclaration de non-
 * condamnation, attestation au conjoint) : celui de l'acte de naissance, même
 * quand on porte un nom d'usage.
 */
function identiteNaissance(i) {
    return { civilite: i.civilite, prenoms: i.prenoms, nom: i.nomNaissance || i.nom };
}
function sortieNaissance(v) {
    return defini({
        civilite: v.civilite === "madame" || v.civilite === "monsieur" ? v.civilite : null,
        prenoms: texte(v.prenoms),
        nomNaissance: texte(v.nom),
    });
}
function adresseStructuree(i) {
    return { voie: i.voie, complement: i.complement, codePostal: i.codePostal, commune: i.commune };
}
function sortieCommune(v) {
    return defini({
        civilite: v.civilite === "madame" || v.civilite === "monsieur" ? v.civilite : null,
        prenoms: texte(v.prenoms),
        nom: texte(v.nom),
    });
}
function sortieAdresse(v) {
    return defini({
        voie: texte(v.voie),
        complement: texte(v.complement),
        codePostal: texte(v.codePostal),
        commune: texte(v.commune),
    });
}

export const TRADUCTEURS_IDENTITE = {
    DeclarationNonCondamnation: {
        entree: (i) => ({
            ...identiteNaissance(i),
            dateDeNaissance: i.dateNaissance,
            villeDeNaissance: i.villeNaissance,
            paysDeNaissance: i.paysNaissance,
            ...adresseStructuree(i),
        }),
        sortie: (v) =>
            defini({
                ...sortieNaissance(v),
                dateNaissance: dateValide(v.dateDeNaissance),
                villeNaissance: texte(v.villeDeNaissance),
                paysNaissance: texte(v.paysDeNaissance),
                ...sortieAdresse(v),
            }),
    },
    AttestationInformationConjoint: {
        entree: (i) => identiteNaissance(i),
        sortie: (v) => sortieNaissance(v),
    },
    AutorisationDomiciliation: {
        entree: (i) => ({
            ...identiteCommune(i),
            dateDeNaissance: i.dateNaissance,
            villeDeNaissance: i.villeNaissance,
            paysDeNaissance: i.paysNaissance,
        }),
        sortie: (v) =>
            defini({
                ...sortieCommune(v),
                dateNaissance: dateValide(v.dateDeNaissance),
                villeNaissance: texte(v.villeDeNaissance),
                paysNaissance: texte(v.paysDeNaissance),
            }),
    },
    DeclarationInitialeCfe: {
        // Ce formulaire n'a pas de champ de complément : il rejoint la voie.
        // Et l'adresse qu'il demande est celle de l'établissement, qui n'est
        // pas toujours le domicile : elle ne remonte pas dans l'identité.
        entree: (i) => ({
            prenoms: i.prenoms,
            nom: nomUsuel(i),
            siret: siretComplet(i),
            codeApe: i.codeApe,
            voie: [i.voie, i.complement].filter(Boolean).join(", ") || null,
            codePostal: i.codePostal,
            commune: i.commune,
        }),
        sortie: (v) =>
            defini({
                prenoms: texte(v.prenoms),
                nom: texte(v.nom),
                siret: typeof v.siret === "string" && /^\d{14}$/.test(v.siret.replace(/\s/g, "")) ? v.siret.replace(/\s/g, "") : null,
                codeApe: texte(v.codeApe, 8),
            }),
    },
    DemandeAcre: {
        entree: (i) => ({
            sexe: i.civilite === "madame" ? "F" : i.civilite === "monsieur" ? "M" : null,
            nomNaissance: i.nomNaissance || i.nom,
            nomUsage: i.nomNaissance && i.nom && i.nomNaissance !== i.nom ? i.nom : null,
            prenoms: i.prenoms,
            nationalite: i.nationalite,
            dateNaissance: i.dateNaissance,
            mail: i.email,
            telephone: i.telephone,
            adresse: [i.voie, i.complement].filter(Boolean).join(", ") || null,
            cp: i.codePostal,
            ville: i.commune,
        }),
        // Le numéro de sécurité sociale n'est jamais repris.
        sortie: (v) =>
            defini({
                civilite: v.sexe === "F" ? "madame" : v.sexe === "M" ? "monsieur" : null,
                nomNaissance: texte(v.nomNaissance),
                nom: texte(v.nomUsage) || texte(v.nomNaissance),
                prenoms: texte(v.prenoms),
                nationalite: texte(v.nationalite),
                dateNaissance: dateValide(v.dateNaissance),
                email: texte(v.mail),
                telephone: texte(v.telephone),
                codePostal: texte(v.cp),
                commune: texte(v.ville),
            }),
    },
    ReclamationCfe: {
        // Pas de champ de complément ici non plus : il rejoint la voie, et
        // l'adresse ne remonte donc pas (« voie, complément » n'est pas une voie).
        entree: (i) => ({
            prenoms: i.prenoms,
            nom: nomUsuel(i),
            siret: siretComplet(i),
            voie: [i.voie, i.complement].filter(Boolean).join(", ") || null,
            codePostal: i.codePostal,
            commune: i.commune,
        }),
        sortie: (v) =>
            defini({
                prenoms: texte(v.prenoms),
                nom: texte(v.nom),
                siret: typeof v.siret === "string" && /^\d{14}$/.test(v.siret.replace(/\s/g, "")) ? v.siret.replace(/\s/g, "") : null,
                codePostal: texte(v.codePostal),
                commune: texte(v.commune),
            }),
    },
    RevisionCodeApe: {
        entree: (i) => ({ siren: sirenDe(i), prenoms: i.prenoms, nom: nomUsuel(i), mail: i.email, telephone: i.telephone }),
        sortie: (v) =>
            defini({
                prenoms: texte(v.prenoms),
                nom: texte(v.nom),
                email: texte(v.mail),
                telephone: texte(v.telephone),
            }),
    },
    DeclarationActiviteFormation: {
        entree: (i) => ({ prenoms: i.prenoms, nom: nomUsuel(i), siret: siretComplet(i) }),
        sortie: (v) =>
            defini({
                prenoms: texte(v.prenoms),
                nom: texte(v.nom),
                siret: typeof v.siret === "string" && /^\d{14}$/.test(v.siret.replace(/\s/g, "")) ? v.siret.replace(/\s/g, "") : null,
            }),
    },
    CarteCommercantAmbulant: {
        entree: (i) => ({
            nomNaissance: i.nomNaissance || i.nom,
            nomUsage: i.nomNaissance && i.nom && i.nomNaissance !== i.nom ? i.nom : null,
            prenoms: i.prenoms,
            dateNaissance: i.dateNaissance,
            lieuNaissance: [i.villeNaissance, i.paysNaissance].filter(Boolean).join(", ") || null,
            nationalite: i.nationalite,
            domicile: adresseEnLigne(i),
        }),
        sortie: (v) =>
            defini({
                nomNaissance: texte(v.nomNaissance),
                nom: texte(v.nomUsage) || texte(v.nomNaissance),
                prenoms: texte(v.prenoms),
                dateNaissance: dateValide(v.dateNaissance),
                nationalite: texte(v.nationalite),
            }),
    },
    FactureImpayee: {
        entree: (i) => ({ prenoms: i.prenoms, nom: nomUsuel(i), adresse: adresseEnLignes(i) }),
        sortie: (v) => defini({ prenoms: texte(v.prenoms), nom: texte(v.nom) }),
    },
    Devis: {
        entree: (i) => ({ prenoms: i.prenoms, nom: nomUsuel(i), adresse: adresseEnLignes(i), siret: siretComplet(i) }),
        sortie: (v) =>
            defini({
                prenoms: texte(v.prenoms),
                nom: texte(v.nom),
                siret: typeof v.siret === "string" && /^\d{14}$/.test(v.siret.replace(/\s/g, "")) ? v.siret.replace(/\s/g, "") : null,
            }),
    },
    DecisionAssocieUnique: {
        entree: (i) => ({
            civilite: i.civilite,
            prenoms: i.prenoms,
            nom: nomUsuel(i),
            adresseAssocie: adresseEnLigne(i),
        }),
        sortie: (v) => sortieCommune(v),
    },
};

/** L'année de référence d'un calcul : celle du jour, lue à midi. */
function contexte(aujourdhui) {
    const d = aujourdhui ? new Date(aujourdhui) : new Date();
    return { annee: Number.isNaN(d.getTime()) ? new Date().getFullYear() : d.getFullYear() };
}

/** Les valeurs de départ d'un outil. Un objet vide quand il n'y a rien à reprendre. */
export function valeursPourOutil(composant, dossier, aujourdhui) {
    const t = TRADUCTEURS[composant];
    if (!t || !dossier) return {};
    return defini(t.entree(nettoyerDossier(dossier), contexte(aujourdhui)));
}

export function valeursIdentitePourOutil(composant, identite) {
    const t = TRADUCTEURS_IDENTITE[composant];
    if (!t || !identite) return {};
    return defini(t.entree(nettoyerIdentite(identite)));
}

/** Ce que les réponses d'un outil disent du dossier, nettoyé. */
export function dossierDepuisOutil(composant, valeurs, dossier) {
    const t = TRADUCTEURS[composant];
    if (!t || !valeurs) return {};
    const d = nettoyerDossier(dossier);
    const brut = t.sortie(valeurs, d);
    // On repasse par le nettoyage pour ne garder que des valeurs permises.
    const propre = nettoyerDossier({ ...DOSSIER_VIDE, ...brut });
    return defini(Object.fromEntries(Object.keys(brut).map((c) => [c, propre[c]])));
}

export function identiteDepuisOutil(composant, valeurs) {
    const t = TRADUCTEURS_IDENTITE[composant];
    if (!t || !valeurs) return {};
    const brut = t.sortie(valeurs);
    const propre = nettoyerIdentite({ ...IDENTITE_VIDE, ...brut });
    return defini(Object.fromEntries(Object.keys(brut).map((c) => [c, propre[c]])));
}

/** Les champs dont la valeur proposée diffère de celle du dossier. */
export function differences(actuel, propose) {
    return Object.entries(propose || {})
        .filter(([c, v]) => v !== null && v !== undefined && (actuel?.[c] ?? null) !== v)
        .map(([champ, valeur]) => ({ champ, avant: actuel?.[champ] ?? null, apres: valeur }));
}

/** Les outils que le dossier sait remplir. */
export const OUTILS_REMPLIS = Object.keys(TRADUCTEURS);
export const FORMULAIRES_REMPLIS = Object.keys(TRADUCTEURS_IDENTITE);

/**
 * La reprise d'un parcours : sauter chaque question dont la réponse est déjà
 * connue, et s'arrêter à la première qui ne l'est pas, ou au résultat.
 *
 * @param etapes la suite des écrans que le parcours traverserait avec ces
 *        valeurs, dans l'ordre, chacun avec `connu` : [{ etape, connu }].
 *        Le dernier est l'écran où l'on arrive quand tout est connu.
 * @returns { etape, pile } : l'écran d'arrivée et ceux qu'un retour dépilera,
 *          ou null quand la première question n'est pas connue (rien à
 *          sauter, le parcours part de son début).
 */
export function reprendre(etapes) {
    if (!Array.isArray(etapes) || !etapes.length || !etapes[0].connu) return null;
    const pile = [];
    for (let i = 0; i < etapes.length; i++) {
        const e = etapes[i];
        if (!e.connu || i === etapes.length - 1) return { etape: e.etape, pile };
        pile.push(e.etape);
    }
    return null;
}

/**
 * Les outils qu'une réponse du dossier remplit, calculés et non écrits : un
 * outil lit un champ si ses valeurs de départ changent quand on le retire
 * d'un dossier complet. Le chiffre annoncé sous chaque question (« cette
 * réponse remplit 19 outils ») reste ainsi juste quand un traducteur change.
 */
const DOSSIERS_COMPLETS = [
    {
        statut: "micro",
        activite: "services",
        artisan: true,
        dateDebut: "2025-03-02",
        caCetteAnnee: 48000,
        caAnDernier: 31000,
        enCouple: true,
        enfants: 1,
        autresRevenus: 20000,
        objectifMensuel: 2800,
        fraisMensuels: 250,
        tarifJournalier: 400,
        optionFiscale: "bareme",
    },
    { statut: "eurl", activite: "bnc", dateDebut: "2024-01-15", caCetteAnnee: 90000, enCouple: false, enfants: 0, autresRevenus: 0, objectifMensuel: 4000, fraisMensuels: 500 },
    { statut: "reel", activite: "vente", dateDebut: "2023-05-01", caCetteAnnee: 120000, enCouple: false, enfants: 2, autresRevenus: 0, objectifMensuel: 3000, fraisMensuels: 2000 },
    { statut: "sasu", activite: "cipav", dateDebut: "2026-02-01", caCetteAnnee: 70000, enCouple: true, enfants: 0, autresRevenus: 0, objectifMensuel: 3500, fraisMensuels: 300 },
    { statut: "eurlIr", activite: "services", dateDebut: "2022-01-01", caCetteAnnee: 60000, enCouple: false, enfants: 0 },
    { statut: "projet", activite: "bnc", caCetteAnnee: 30000, enCouple: false, enfants: 0, autresRevenus: 0, objectifMensuel: 2000 },
];

export function outilsQuiLisent(champs, aujourdhui = "2026-06-15") {
    const liste = Array.isArray(champs) ? champs : [champs];
    const lecteurs = new Set();
    for (const complet of DOSSIERS_COMPLETS) {
        const sans = { ...complet };
        for (const c of liste) sans[c] = null;
        for (const composant of OUTILS_REMPLIS) {
            const avec = JSON.stringify(valeursPourOutil(composant, complet, aujourdhui));
            const moins = JSON.stringify(valeursPourOutil(composant, sans, aujourdhui));
            if (avec !== moins) lecteurs.add(composant);
        }
    }
    return [...lecteurs];
}

/**
 * Les réponses du dossier qu'un outil a réellement reprises : celles dont le
 * retrait change ses valeurs de départ. C'est ce que son bandeau affiche
 * (« Rempli avec votre dossier : micro-entreprise · 48 000 € »), et ce qu'on
 * propose d'y corriger.
 */
export function champsLusPar(composant, dossier, aujourdhui) {
    const d = nettoyerDossier(dossier);
    const reference = JSON.stringify(valeursPourOutil(composant, d, aujourdhui));
    return CHAMPS_SITUATION.filter((champ) => {
        if (d[champ] === null) return false;
        return JSON.stringify(valeursPourOutil(composant, { ...d, [champ]: null }, aujourdhui)) !== reference;
    });
}
